import React, { FC, useMemo } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { DomotzRtd, DomotzRtdentry } from '../../../interfaces/NetworkDevices';
import Panel from '../../boxes/Panel';
import Typography from '../../typography/Typography';

/** * SHARED CONSTANTS
 */
const RTD_COLORS = {
  latencyRange: '#a78bfa', // Violet 400 (Softer Range)
  latencyMedian: '#6366f1', // Indigo 500 (Primary Focus)
  packetLoss: '#f472b6', // Rose 400 (Desaturated, Softer Alert Color)
};

export const calculateSummaryStats = (data: DomotzRtdentry[]) => {
  if (!data || data.length === 0)
    return {
      overallMinLatency: 0,
      overallMaxLatency: 0,
      overallAverageMedianLatency: 0,
      totalLost: 0,
      totalSent: 0,
      overallPacketLoss: 0,
      totalTests: 0,
    };

  let minLatencies: number[] = [];
  let maxLatencies: number[] = [];
  let medianLatencies: number[] = [];
  let allLatencies: number[] = [];
  let totalLost = 0;
  let totalSent = 0;

  data.forEach((item: DomotzRtdentry) => {
    minLatencies.push(item.min);
    medianLatencies.push(item.median);
    maxLatencies.push(item.max);
    allLatencies.push(item.min, item.median, item.max);
    totalLost += item.lost_packet_count;
    totalSent += item.sent_packet_count;
  });

  const absoluteMaxLatency = Math.max(...allLatencies);
  const overallAverageMedianLatency =
    medianLatencies.reduce((a, b) => a + b, 0) / medianLatencies.length;
  const overallPacketLoss = totalSent > 0 ? (totalLost / totalSent) * 100 : 0;

  return {
    overallMinLatency: Math.min(...minLatencies),
    overallMaxLatency: Math.max(...maxLatencies),
    absoluteMaxLatency,
    overallAverageMedianLatency,
    totalLost,
    totalSent,
    overallPacketLoss,
    totalTests: data.length,
  };
};

export const RtdChartVisualization: FC<DomotzRtd> = ({ history }) => {
  const stats = useMemo(() => calculateSummaryStats(history), [history]);

  const processedData = useMemo(() => {
    if (!history || history.length === 0) return [];

    return [...history]
      .sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      )
      .map((item) => ({
        // Localized for Belgium
        time: new Date(item.timestamp).toLocaleString('en-BE', {
          timeZone: 'Europe/Brussels',
          day: '2-digit',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }),
        median: item.median,
        range: [item.min, item.max],
        loss:
          item.sent_packet_count > 0
            ? (item.lost_packet_count / item.sent_packet_count) * 100
            : 0,
      }));
  }, [history]);

  // Dynamic Y-Axis Scale
  const maxLatencyDomain = Math.max(
    15,
    stats.absoluteMaxLatency ? stats.absoluteMaxLatency * 1.1 : 0
  );

  if (history.length === 0)
    return (
      <div className="h-[450px] flex items-center justify-center text-gray-400 italic">
        No data available.
      </div>
    );

  return (
    <Panel
      classNames="w-full py-6 transition-all duration-300"
      background="transparent"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <Typography tag="h2" type="subtitle">
            Stability
          </Typography>
          <Typography tag="p" type="label">
            Latency & Packet Loss
          </Typography>
        </div>

        {/* Custom Legend */}
        <div className="flex gap-4 text-[9px] font-black uppercase tracking-tighter opacity-60">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-sm bg-indigo-500 opacity-20" />
            Range
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 bg-indigo-500" />
            Median
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 bg-red-500" />
            Loss
          </div>
        </div>
      </div>

      <div className="h-[400px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={processedData}
            margin={{ top: 0, right: 0, left: -25, bottom: 0 }}
          >
            <CartesianGrid
              strokeDasharray="0"
              vertical={false}
              stroke="currentColor"
              className="opacity-10"
            />

            <XAxis
              dataKey="time"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: 'currentColor', fontWeight: 700 }}
              className="opacity-40"
              dy={15}
              minTickGap={40}
            />

            <YAxis
              yAxisId="latency"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: 'currentColor', fontWeight: 700 }}
              className="opacity-40"
              domain={[0, maxLatencyDomain]}
              tickFormatter={(val) => `${val}ms`}
            />

            <YAxis
              yAxisId="loss"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: 10,
                fill: RTD_COLORS.packetLoss,
                fontWeight: 700,
              }}
              domain={[0, (max: number) => Math.max(max + 1, 5)]}
              tickFormatter={(val) => `${val.toFixed(0)}%`}
            />

            <Tooltip
              content={<CustomRtdTooltip />}
              cursor={{ stroke: 'currentColor', opacity: 0.1 }}
            />

            {/* 1. Latency Jitter Range (The Background) */}
            <Area
              yAxisId="latency"
              dataKey="range"
              type="monotone"
              stroke="none"
              fill="url(#rangeGrad)"
              fillOpacity={1}
              animationDuration={1000}
            />

            {/* 2. Packet Loss (Behind Median) - Softer Rose Color */}
            <Line
              yAxisId="loss"
              type="stepAfter"
              dataKey="loss"
              stroke={RTD_COLORS.packetLoss}
              strokeWidth={1.5}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0, fill: RTD_COLORS.packetLoss }}
              className="opacity-60 dark:opacity-80"
            />

            {/* 3. Median Latency (Drawn LAST = On Front) */}
            <Line
              yAxisId="latency"
              type="monotone"
              dataKey="median"
              stroke={RTD_COLORS.latencyMedian}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 8, strokeOpacity: 0.15 }}
              animationDuration={1200}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
};

/**
 * TOOLTIP SUB-COMPONENT
 */
const CustomRtdTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const range = payload.find((p: any) => p.dataKey === 'range')?.value || [
      0, 0,
    ];
    const median = payload.find((p: any) => p.dataKey === 'median')?.value;
    const loss = payload.find((p: any) => p.dataKey === 'loss')?.value;

    return (
      <div className="backdrop-blur-md bg-white/90 dark:bg-black/90 p-4 border border-gray-200 dark:border-white/20 shadow-2xl rounded-2xl text-black dark:text-white min-w-[180px]">
        <p className="text-[9px] font-black uppercase tracking-widest opacity-40 mb-3">
          {label}
        </p>
        <div className="space-y-2">
          <div className="flex justify-between items-center text-[10px] font-bold">
            <span className="opacity-50">MIN/MAX</span>
            <span>
              {range[0]} - {range[1]}ms
            </span>
          </div>
          <div
            className="flex justify-between items-center text-sm font-black"
            style={{ color: RTD_COLORS.latencyMedian }}
          >
            <span>MEDIAN</span>
            <span>{median}ms</span>
          </div>
          <div className="h-[1px] bg-currentColor opacity-10 my-1" />
          <div
            className="flex justify-between items-center text-xs font-black"
            style={{ color: RTD_COLORS.packetLoss }}
          >
            <span>PACKET LOSS</span>
            <span>{loss.toFixed(2)}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};
